/**
 * Grouping the timeline into "Hôm nay / Hôm qua / <ngày>" (spec v2 22.4).
 *
 * The day a transaction belongs to is the day the FAMILY lived, so every boundary here is
 * computed in the household timezone — never in the device's. A phone left on UTC would
 * otherwise file an evening expense under tomorrow.
 *
 * Pure and timezone-explicit so it can be unit tested without a clock (skill 8.1).
 */
import type { TransactionLine } from '../data/timelineRepository.js';

export type TimelineGroup<T> = Readonly<{
  /** YYYY-MM-DD in the household timezone — stable key, also the sort order. */
  dayKey: string;
  label: string;
  items: readonly T[];
}>;

/** YYYY-MM-DD of an instant, as seen in the given timezone. */
export function dayKeyOf(instant: string, timeZone: string): string {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return formatter.format(new Date(instant));
}

/** The calendar day before a YYYY-MM-DD key. Date arithmetic, deliberately not money. */
function previousDayKey(dayKey: string): string {
  const date = new Date(`${dayKey}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() - 1);
  return date.toISOString().slice(0, 10);
}

const LONG_DATE = new Intl.DateTimeFormat('vi-VN', {
  weekday: 'long',
  day: 'numeric',
  month: 'numeric',
  timeZone: 'UTC',
});

const LONG_DATE_WITH_YEAR = new Intl.DateTimeFormat('vi-VN', {
  day: 'numeric',
  month: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

/**
 * "Hôm nay" / "Hôm qua" for the two days a person thinks in, then the weekday and date.
 * Anything from another year keeps the year, because "thứ Ba, 3/2" in a five-year-old ledger
 * is not an answer.
 */
export function dayLabelOf(dayKey: string, todayKey: string): string {
  if (dayKey === todayKey) return 'Hôm nay';
  if (dayKey === previousDayKey(todayKey)) return 'Hôm qua';

  const date = new Date(`${dayKey}T00:00:00Z`);
  const sameYear = dayKey.slice(0, 4) === todayKey.slice(0, 4);
  return sameYear ? LONG_DATE.format(date) : LONG_DATE_WITH_YEAR.format(date);
}

/** The clock time shown on a row, in the household timezone. */
export function timeLabelOf(instant: string, timeZone: string): string {
  return new Intl.DateTimeFormat('vi-VN', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(instant));
}

/** One entry as the timeline renders it: an ordinary movement, or a split with its legs. */
export type TimelineEntry = Readonly<{
  /** Journal entry id — what editing, voiding and the list key use. */
  id: string;
  /** Every leg that came back, in server order. Exactly one for an ordinary movement. */
  legs: readonly TransactionLine[];
  /** Entry-level facts (date, note, source): identical on every leg, read from the first. */
  head: TransactionLine;
  /** A split is shown as a card listing its legs (`line_count` counts the credit line too). */
  isSplit: boolean;
  /**
   * Whether every leg of the entry is present. A category filter matches only the legs that
   * carry it, and then the entry total would not be the sum of what is on screen — so the
   * screen shows no total rather than one the visible rows contradict.
   */
  isComplete: boolean;
}>;

/**
 * Folds the legs of one entry back into a single item.
 *
 * Legs of an entry are adjacent in the server's order (same instant, same entry id), so a
 * single pass over the flattened list is enough — and it keeps working when a page boundary
 * falls in the middle of a split, because the next page continues the same run.
 */
export function groupLinesByEntry(lines: readonly TransactionLine[]): readonly TimelineEntry[] {
  const entries: Array<{ id: string; legs: TransactionLine[] }> = [];

  for (const line of lines) {
    const last = entries.at(-1);
    if (last !== undefined && last.id === line.id) last.legs.push(line);
    else entries.push({ id: line.id, legs: [line] });
  }

  return entries.map(({ id, legs }) => {
    const head = legs[0]!;
    return {
      id,
      legs,
      head,
      isSplit: head.lineCount > 2,
      // line_count counts the single credit line as well, so the debit legs are one fewer.
      isComplete: legs.length === head.lineCount - 1,
    };
  });
}

/**
 * Groups entries by day, preserving the order they arrive in (the query already sorts them
 * newest first — this never re-sorts, so paging cannot shuffle the list).
 */
export function groupTransactionsByDay(
  lines: readonly TransactionLine[],
  options: { timeZone: string; now: Date },
): readonly TimelineGroup<TimelineEntry>[] {
  const todayKey = dayKeyOf(options.now.toISOString(), options.timeZone);
  const groups: Array<{ dayKey: string; label: string; items: TimelineEntry[] }> = [];

  for (const entry of groupLinesByEntry(lines)) {
    const dayKey = dayKeyOf(entry.head.occurredAt, options.timeZone);
    const last = groups.at(-1);
    if (last !== undefined && last.dayKey === dayKey) {
      last.items.push(entry);
    } else {
      groups.push({ dayKey, label: dayLabelOf(dayKey, todayKey), items: [entry] });
    }
  }

  return groups;
}
